import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import {
  PwrConfigResponse,
  PwrRenderOptions,
  PwrReviewSubmissionData,
} from './../shared/models/power-reviews/pwr-render-options';
import { PowerReviewService } from './../shared/services/power-review.service';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import { PageInfoService, PanAppState } from '@panamax/app-state';
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { PATHS } from '../shared/constants/paths';
import { first, switchMap, take } from 'rxjs/operators';
import { ProductDetailViewModel } from '../product-detail/models/product-detail-view-model';
import { ProductDetailService } from '../product-detail/services/product-detail.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { Crumb } from '../shared/components/breadcrumbs/breadcrumbs.component';
import { UserDataService } from '../user/services/user-data.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-review-products',
  templateUrl: './review-products.page.html',
  styleUrls: [
    './review-products.page.scss',
    './review-products-submission.page.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ReviewProductsPage
  implements ViewWillLeave, ViewWillEnter, OnDestroy
{
  productNumber: string;
  appName: string;
  breadcrumbs: Crumb[];
  // TODO: Pending to check if it'll be necessary when Write Review is finally implemented
  productChangeSubject = new Subject<number>();
  product: ProductDetailViewModel;
  continueShoppingButton: HTMLElement;
  title: string;
  subtitle: string;
  // subscriptions
  productSubscription$: Subscription = new Subscription();
  userEmail: string;

  user$: Subscription;

  constructor(
    private pageInfoService: PageInfoService,
    private powerReviewService: PowerReviewService,
    private productDetailService: ProductDetailService,
    private navigationHelperService: NavigationHelperService,
    private analyticsService: ProductAnalyticsService,
    private elRef: ElementRef,
    readonly panAppState: PanAppState,
    private userDataService: UserDataService,
    private translateService: TranslateService,
  ) {}

  ionViewWillEnter() {
    this.productNumber = this.pageInfoService.getUrlQueryParamter('pr_page_id');
    this.appName = this.pageInfoService.getUrlQueryParamter('appName');
    this.setUserEmail();
    this.productSubscription$ = this.productDetailService
      .loadProductDetail$(+this.productNumber)
      .pipe(
        switchMap(x =>
          this.productDetailService.getProductDetailViewModel(
            +this.productNumber,
          ),
        ),
        first(product => !!product),
      )
      .subscribe(async p => {
        this.product = p;
        if (this.appName === 'askQuestion') {
          this.panAppState.setPageTitle(
            this.translateService.instant(PageTitle.askAQuestionPage),
          );
          await this.renderAskAQuestion();
          this.title = PageTitle.askAQuestionPage;

          this.breadcrumbs = [
            {
              label: this.transformHelper(
                this.product?.summary?.productDescTxtl,
              ),
              url: '/products/' + this.product?.productNumber,
              active: false,
            },
            {
              label: PageTitle.askAQuestionPage,
              url: '',
              active: true,
            },
          ];
        } else {
          this.panAppState.setPageTitle(PageTitle.writeAProductReview);
          await this.renderWriteReviews();
          this.title = 'i18n.writeReviewPage.writeAReviewTitle';
          this.subtitle = 'i18n.writeReviewPage.writeAReviewSubtitle';
        }
      });
  }

  goToHomePage = () => {
    this.navigationHelperService.routeTo(PATHS.HOME);
  };

  renderWriteReviews(): Promise<void> {
    return new Promise(res => {
      const options: PwrRenderOptions = {
        ...this.powerReviewService.getBaseRenderOptions(),
        page_id: `${this.productNumber}`,
        product: this.powerReviewService.buildFeedlessProduct(this.product),
        return_url: `/${PATHS.HOME}`,
        components: {
          // Param to set write behavior
          Write: 'pwr-write-review',
        },
        on_render: (config, data) => {
          // Write a review Page
          document
            .getElementsByClassName('pr-alert-container')[0]
            .setAttribute('style', 'display: none');
          document
            .getElementsByClassName('pr-header')[0]
            .setAttribute('style', 'display: none');
          document
            .getElementsByClassName('pr-war ')[0]
            .setAttribute('style', 'max-width: 1200px');
          // Update Texts
          // Star Rating
          document
            .getElementsByClassName(
              'pr-form-group form-group pr-rating-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent =
            'How would you rate this item?';
          // Product's Review
          document
            .getElementsByClassName(
              'pr-form-group form-group pr-comments-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent =
            'Let us know what you thought of this product.';
          // Recommendation
          document
            .getElementsByClassName(
              'pr-form-group form-group pr-bottomline-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent =
            'Would you recommend this item to others?';
          // Update Submit button text
          document.getElementsByClassName(
            'pr-pull-left pr-btn pr-btn-default pr-btn-review pr-accessible-btn',
          )[0].textContent = 'Submit';

          this.analyticsService.trackReviewProductsLoad(
            this.product?.summary?.divisionNumber,
            this.product?.productNumber,
            this.product?.trackingAttributes,
          );
        },
        on_submit: (
          config: PwrConfigResponse,
          data: PwrReviewSubmissionData,
        ) => {
          // Submission Page
          document.getElementsByTagName('app-product-header-card')[0][
            'style'
          ].display = 'none';
          document.getElementsByClassName('page-title-oswald')[0][
            'style'
          ].display = 'none';
          document.getElementsByClassName('review-products-subtitle')[0][
            'style'
          ].display = 'none';
          if (data?.fields?.length) {
            document
              .getElementsByClassName('thank-you-page')[0]
              .getElementsByClassName('title')[0].innerHTML =
              'Thank you for reviewing!';
            document
              .getElementsByClassName('thank-you-page')[0]
              .getElementsByClassName('subtitle')[0].innerHTML =
              'Our team is at work processing your review and will publish it as soon as possible.';
            // If Recommended Product was filled (Optional in Write a Review Form)
            if (data.fields.some(f => f.key === 'bottomline')) {
              document
                .getElementsByClassName('thank-you-page')[0]
                .getElementsByClassName('pr-dark')[0].innerHTML =
                'Would you recommend this product to others?';
            }

            this.continueShoppingButton =
              this.elRef.nativeElement.querySelector(
                '.thank-you-page .header h5',
              ) as HTMLElement;
            this.continueShoppingButton.addEventListener(
              'click',
              this.goToHomePage,
            );

            this.analyticsService.trackReviewProductsSubmit(
              this.product?.summary?.divisionNumber,
              this.product?.productNumber,
              this.product?.trackingAttributes,
              document.getElementsByClassName('pr-star-v4-100-filled')?.length,
            );
          }
        },
      };
      setTimeout(() => {
        this.powerReviewService.renderComponents(options);
        res();
      }, 500);
    });
  }

  renderAskAQuestion(): Promise<void> {
    return new Promise(res => {
      const options: PwrRenderOptions = {
        ...this.powerReviewService.getBaseRenderOptions(),
        page_id: `${this.productNumber}`,
        return_url: `/${PATHS.HOME}`,
        merchant_user_email: this.userEmail,
        components: {
          Write: 'pwr-ask-a-question',
        },
        on_render: (config, data) => {
          document
            .getElementsByClassName('pr-header')[0]
            .setAttribute('style', 'display: none');
          document
            .getElementsByClassName(
              'pr-form-group form-group questionText-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent =
            'Let us know what your question about this product is.';
          document
            .getElementById('questionText-input')
            .setAttribute(
              'placeholder',
              'Is this product seasoned? Is this item easy to use? What are the dimensions of this item?',
            );
          document
            .getElementsByClassName(
              'pr-form-group form-group authorEmail-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent = 'Email';
          document
            .getElementById('authorEmail-input')
            .setAttribute(
              'placeholder',
              'Choose an email that will appear with your question',
            );
          document
            .getElementsByClassName(
              'pr-form-group form-group authorName-form-group',
            )[0]
            .getElementsByTagName('span')[0].textContent = 'Nickname';
          document
            .getElementById('authorName-input')
            .setAttribute(
              'placeholder',
              'Choose a nickname that will appear with your question',
            );
          document.getElementsByClassName(
            'pr-pull-left pr-btn pr-btn-default pr-btn-review pr-accessible-btn',
          )[0].textContent = 'Submit';

          this.analyticsService.trackQAPageLoad(
            this.product?.summary?.divisionNumber,
            this.product?.productNumber,
            this.product?.trackingAttributes,
          );
        },
        on_submit: (config, data) => {
          document.getElementsByTagName('app-product-header-card')[0][
            'style'
          ].display = 'none';
          document.getElementsByClassName('page-title-oswald')[0][
            'style'
          ].display = 'none';

          document.getElementsByClassName('breadcrumbs-container')[0][
            'style'
          ].display = 'none';
          document.getElementsByClassName('pr-qa-question-bubble')[0][
            'style'
          ].display = 'none';

          if (data?.fields?.length) {
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByClassName('pr-header-title')[0].innerHTML =
              'Thank you for your question!';
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByTagName('span')[0].textContent =
              'Our team is working to answer your questions and will update you as soon as possible.';
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByTagName('a')[0].textContent = 'Continue Shopping';
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByTagName('a')[0]
              .setAttribute('href', PATHS.HOME);
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByClassName('pr-qa-question-bubble')[0].innerHTML =
              'Question';
            document
              .getElementsByClassName('pr-waqwaa')[0]
              .getElementsByClassName(
                'pr-qa-display-text-content',
              )[0].innerHTML = `<div class='custom-display-container'>
                    <h4 class='custom-display-label'>Question</h4>
                    <span class='custom-display-body'>${data.fields[2].value}</span>
               </div>
               <div class='custom-display-container'>
                    <h4 class='custom-display-label'>Email</h4>
                    <span class='custom-display-body'>${data.fields[0].value}</span>
               </div>
              <div class='custom-display-container'>
                  <h4 class='custom-display-label'>Nickname</h4>
                  <span class='custom-display-body'>${data.fields[1].value}</span>
              </div>`;

            this.analyticsService.trackSubmitQuestionEvent(
              this.product?.summary?.divisionNumber,
              this.product?.productNumber,
              this.product?.trackingAttributes,
            );
          }
        },
      };
      setTimeout(() => {
        this.powerReviewService.renderComponents(options);
        res();
      }, 500);
    });
  }

  ionViewWillLeave(): void {
    this.user$?.unsubscribe();
    this.productSubscription$?.unsubscribe();
    this.continueShoppingButton?.removeEventListener(
      'click',
      this.goToHomePage,
    );
  }

  transformHelper(sentence: string): string {
    const transformed = sentence
      .toLocaleLowerCase()
      .split(' ')
      .slice(0, 4)
      .map(word => {
        return word[0].toUpperCase() + word.substring(1);
      })
      .join(' ')
      .concat('...');
    return transformed;
  }

  setUserEmail() {
    this.user$ = this.userDataService
      .getByToken()
      .pipe(take(1))
      .subscribe(user => {
        this.userEmail = user?.email;
      });
  }

  ngOnDestroy(): void {
    this.user$?.unsubscribe();
    this.productSubscription$?.unsubscribe();
  }
}
